/**
 * 数据上报模块
 * 优先级：fetch + keepalive -> XMLHttpRequest
 */

import type { TrackData } from '../types'
import { getSignHeaders } from '../utils/sign'
import { serializeTrackData } from '../utils/transform'

/** 请求默认超时时间（毫秒） */
const TIMEOUT_MS = 3000

export interface ReporterConfig {
  reportUrl: string
  /** 批量上报地址，当微任务内多次调用 report 时使用此地址 */
  reportBatchUrl?: string
  debug?: boolean
  sampleRate?: number
  /** 是否携带凭证（cookie），默认 false 不携带 */
  withCredentials?: boolean
  /** 是否启用 fetch keepalive，默认 true */
  supportKeepAlive?: boolean
}

/**
 * Reporter 数据上报类
 */
export class Reporter {
  private readonly config: ReporterConfig
  private queue: TrackData[] = []
  private isSending = false

  /** 缓存 XMLHttpRequest 可用性检测结果 */
  private readonly canUseXHR: boolean
  /** 缓存 Fetch API 可用性检测结果 */
  private readonly canUseFetch: boolean

  /** 微任务内暂存的上报数据 */
  private pendingBatch: TrackData[] = []
  /** 是否已调度微任务 */
  private batchScheduled = false

  constructor(config: ReporterConfig) {
    this.config = config
    // 初始化时检测一次，缓存结果
    this.canUseXHR = typeof XMLHttpRequest !== 'undefined'
    this.canUseFetch = typeof fetch === 'function' && typeof AbortController === 'function'
  }

  /**
   * 上报单条数据
   * 优先级：fetch + keepalive -> XMLHttpRequest
   * @param data 上报数据
   * @returns 是否上报成功
   */
  report(data: TrackData): boolean {
    // 采样率检查
    if (this.config.sampleRate !== undefined && this.config.sampleRate < 1) {
      if (Math.random() > this.config.sampleRate) {
        return false
      }
    }


    // 如果配置了批量上报地址，使用微任务合并
    if (this.config.reportBatchUrl) {
      this.pendingBatch.push(data)
      if (!this.batchScheduled) {
        this.batchScheduled = true
        Promise.resolve().then(() => this.flushPendingBatch())
      }
      return true
    }

    // 未配置批量地址，走原有单条上报逻辑
    if (this.sendFetch(data)) {
      return true
    }

    // 降级到 XMLHttpRequest
    return this.sendXHR(data)
  }

  /**
   * 刷新微任务内暂存的批量数据
   */
  private flushPendingBatch(): void {
    this.batchScheduled = false
    const batch = this.pendingBatch
    this.pendingBatch = []

    if (batch.length === 0) return

    if (batch.length === 1) {
      // 只有一条数据，走单条上报（使用 reportUrl）
      this.sendFetch(batch[0]) || this.sendXHR(batch[0])
      return
    }

    // 多条数据，走批量上报（使用 reportBatchUrl）
    this.log('微任务合并批量上报', batch)
    this.sendFetchBatch(batch, true) || this.sendXHRBatch(batch, true)
  }

  /**
   * 批量上报数据
   * 优先级：fetch + keepalive -> XMLHttpRequest
   * @param dataList 数据列表
   * @returns 是否上报成功
   * @deprecated 未使用
   */
  reportBatch(dataList: TrackData[]): boolean {
    if (dataList.length === 0) return true

    // 采样率过滤
    const filteredList = dataList.filter(() => {
      if (this.config.sampleRate !== undefined && this.config.sampleRate < 1) {
        return Math.random() <= this.config.sampleRate
      }
      return true
    })

    if (filteredList.length === 0) return true

    this.log('Reporting batch data', filteredList)

    // 尝试使用 fetch + keepalive
    if (this.sendFetchBatch(filteredList)) {
      return true
    }

    // 降级到 XMLHttpRequest
    return this.sendXHRBatch(filteredList)
  }

  /**
   * 异步上报单条数据（等待实际发送完成）
   * 用于 afterClick 场景：需要确保上报发送后再放行业务回调
   * @param data 上报数据
   * @returns Promise<boolean> - true 表示发送成功，false 表示失败
   */
  reportAsync(data: TrackData): Promise<boolean> {
    // 采样率检查
    if (this.config.sampleRate !== undefined && this.config.sampleRate < 1) {
      if (Math.random() > this.config.sampleRate) {
        this.log('Skipped by sample rate (async)', data)
        return Promise.resolve(false)
      }
    }

    this.log('异步上报数据', data)
    return this.sendFetchAsync(data)
  }

  /**
   * 使用 Fetch 异步上报（等待响应完成）
   */
  private sendFetchAsync(data: TrackData): Promise<boolean> {
    if (!this.canUseFetch) {
      // 降级到同步 XHR（fire-and-forget）
      return Promise.resolve(this.sendXHR(data))
    }

    const timeout = TIMEOUT_MS
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    const payload = data
    const jsonStr = serializeTrackData(payload)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    const signHeaders = getSignHeaders(payload as any, {
      url: this.config.reportUrl,
    })
    Object.assign(headers, signHeaders)

    return fetch(this.config.reportUrl, {
      method: 'POST',
      headers,
      body: jsonStr,
      keepalive: this.config.supportKeepAlive ?? true,
      signal: controller.signal,
    })
      .then(async (response) => {
        clearTimeout(timeoutId)
        const res = await response.json().catch(() => ({}))
        if (res.code == 1) {
          this.log('Fetch 异步上报成功', res)
        } else {
          this.log('Fetch 异步上报失败', res)
        }
        return true
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        if (error.name === 'AbortError') {
          this.log('Fetch 异步上报超时')
        } else {
          this.log('Fetch 异步上报异常', error)
        }
        return false
      })
  }

  /**
   * 加入队列（用于批量上报）
   * @param data 上报数据
   */
  enqueue(data: TrackData): void {
    this.queue.push(data)
  }

  /**
   * 刷新队列，发送所有待上报数据
   * @deprecated 未使用
   */
  flush(): boolean {
    if (this.queue.length === 0 || this.isSending) return true

    this.isSending = true
    const dataToSend = [...this.queue]
    this.queue = []

    const result = this.reportBatch(dataToSend)
    this.isSending = false

    return result
  }

  /**
   * 使用 Fetch + keepalive 上报单条数据
   * 支持超时主动断链，页面卸载时仍能完成请求
   */
  private sendFetch(data: TrackData): boolean {
    return this.sendFetchBatch([data])
  }

  /**
   * 使用 Fetch + keepalive 批量上报数据
   * 支持超时主动断链，页面卸载时仍能完成请求
   */
  private sendFetchBatch(dataList: TrackData[], useBatchUrl = false): boolean {
    if (!this.canUseFetch) {
      return false
    }

    const isBatch = useBatchUrl && !!this.config.reportBatchUrl
    const url = isBatch ? this.config.reportBatchUrl! : this.config.reportUrl
    const timeout = TIMEOUT_MS

    try {
      const payload = isBatch ? {events: dataList} : dataList[0]

      // 创建 AbortController 用于超时控制
      const controller = new AbortController()
      const timeoutId = setTimeout(() => {
        controller.abort()
        this.log(`Fetch 请求超时 (${timeout}ms)，主动断链`)
      }, timeout)

      const jsonStr = serializeTrackData(payload)
      // 构建请求头
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      // 如果启用签名，添加签名头
      const signHeaders = getSignHeaders(payload as any, {
        url,
      })
      Object.assign(headers, signHeaders)

      fetch(url, {
        method: 'POST',
        headers,
        body: jsonStr,
        keepalive: this.config.supportKeepAlive ?? true, // 页面卸载时仍能完成请求
        // credentials: this.config.withCredentials ? 'include' : 'omit',
        signal: controller.signal,
      })
        .then(async (response) => {
          const res = await response.json()
          clearTimeout(timeoutId)
          if (res.code == 1) {
            this.log('Fetch 上报成功', res)
          } else {
            this.log('Fetch 上报失败', res)
          }
        })
        .catch((error) => {
          clearTimeout(timeoutId)
          // 区分超时中断和其他错误
          if (error.name === 'AbortError') {
            this.log('Fetch 请求被中断（超时）')
          } else {
            this.log('Fetch 上报异常', error)
          }
        })

      return true
    } catch (error) {
      this.log('Fetch 上报初始化异常', error)
      return false
    }
  }

  /**
   * 使用 XMLHttpRequest 上报单条数据
   */
  private sendXHR(data: TrackData): boolean {
    return this.sendXHRBatch([data])
  }

  /**
   * 使用 XMLHttpRequest 批量上报数据
   */
  private sendXHRBatch(dataList: TrackData[], useBatchUrl = false): boolean {
    if (!this.canUseXHR) {
      return false
    }

    const isBatch = useBatchUrl && !!this.config.reportBatchUrl
    const url = isBatch ? this.config.reportBatchUrl! : this.config.reportUrl

    try {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url, true)
      xhr.setRequestHeader('Content-Type', 'application/json')
      // 显式设置不携带 cookie（跨域请求时生效）
      xhr.withCredentials = this.config.withCredentials ?? false
      // 设置超时
      xhr.timeout = TIMEOUT_MS

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          if (xhr.status >= 200 && xhr.status < 300) {
            this.log('XHR report success')
          } else {
            this.log('XHR report failed', xhr.status)
          }
        }
      }

      xhr.onerror = () => {
        this.log('XHR report error')
      }

      xhr.ontimeout = () => {
        this.log(`XHR report timeout (${TIMEOUT_MS}ms)`)
      }

      const payload = isBatch ? {events: dataList} : dataList[0]

      // 如果启用签名，添加签名头
      const signHeaders = getSignHeaders(payload as any, {
        url,
      })
      Object.entries(signHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value)
      })

      xhr.send(serializeTrackData(payload))
      return true
    } catch (error) {
      this.log('XHR 上报异常', error)
      return false
    }
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log('%c[Tracker Reporter 日志] %c ' + message, 'background: #aa9514; color: #fff;', 'color: inherit;', data)
    }
  }
}
